<template>
  <div class="e-pdf">
    <div class="container">
      <div class="control-panel">
        <div class="upload-section">
          <button @click="triggerFileInput">上传PDF文件</button>
          <input
              type="file"
              id="pdf-upload"
              accept=".pdf"
              @change="onFileChange"
              style="display: none"
          >
        </div>

        <div class="actions">
          <button
              class="btn btn-success"
              @click="downloadPDF"
              :disabled="!compressedPDF"
          >
            下载压缩后PDF
          </button>
        </div>
      </div>

      <div class="right-panel">
        <div class="header" v-if="compressedPDF">
          <div class="header-content">
            <div class="pdf-info-cards">
              <div class="info-card original">
                <div class="card-header">
                  <span class="icon">📄</span>
                  <span>原PDF信息</span>
                </div>
                <div class="info-item">
                  <span class="info-label">文件名:</span>
                  <span class="info-value">{{ originalFileName }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">文件大小:</span>
                  <span class="info-value">{{ formatFileSize(originalFileSize) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">页数:</span>
                  <span class="info-value">{{ originalPageCount }}</span>
                </div>
              </div>

              <div class="info-card compressed">
                <div class="card-header">
                  <span class="icon">✅</span>
                  <span>压缩后信息</span>
                </div>
                <div class="info-item">
                  <span class="info-label">体积减少:</span>
                  <span class="info-value reduction">{{ sizeReduction }}%</span>
                </div>
                <div class="info-item">
                  <span class="info-label">文件大小:</span>
                  <span class="info-value success">{{ formatFileSize(compressedFileSize) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">页数:</span>
                  <span class="info-value">{{ compressedPageCount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="preview-container">
          <div v-if="!originalPDF" class="upload-prompt">
            <div class="upload-icon">📄</div>
            <p>上传PDF文件开始压缩</p>
          </div>

          <div v-else class="pdf-preview">
            <div class="pdf-preview-box">
              <div class="pdf-icon">📄</div>
            </div>
            <div class="pdf-info">
              <p>PDF文件预览</p>
              <p>文件名: {{ originalFileName }}</p>
              <p>大小: {{ formatFileSize(originalFileSize) }}</p>
              <p>页数: {{ originalPageCount }}</p>
              <p v-if="compressedPDF" class="compression-info">
                压缩后: {{ formatFileSize(compressedFileSize) }} (减少 {{ sizeReduction }}%)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PDFDocument } from 'pdf-lib'
import { compress, CompressionError } from '@quicktoolsone/pdf-compress'

export default {
  name: "EPdf",
  data() {
    return {
      originalPDF: null,
      compressedPDF: null,
      originalFile: null,
      originalFileName: '',
      originalFileSize: 0,
      compressedFileSize: 0,
      originalPageCount: 0,
      compressedPageCount: 0
    };
  },
  computed: {
    sizeReduction() {
      if (!this.compressedFileSize) return 0;
      return Math.round(((this.originalFileSize - this.compressedFileSize) / this.originalFileSize) * 100);
    }
  },
  methods: {
    triggerFileInput() {
      document.getElementById('pdf-upload').click();
    },

    onFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.originalFile = file
        this.processFile(file)
      }
    },

    async processFile(file) {
      // 检查文件类型
      if (!file.type.match('application/pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
        alert('请选择PDF格式的文件');
        return;
      }

      // 检查文件大小（最大1GB）
      if (file.size > 1024 * 1024 * 1024) {
        alert('文件大小不能超过1G');
        return;
      }

      this.originalFileName = file.name;
      this.originalFileSize = file.size;

      try {
        // 读取PDF文件
        const arrayBuffer = await file.arrayBuffer();
        this.originalPDF = arrayBuffer;

        // 获取PDF页数
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        this.originalPageCount = pdfDoc.getPageCount();

        // 上传PDF后自动压缩
        this.compressPDF();
      } catch (error) {
        console.error('处理PDF文件时出错:', error);
        alert('处理PDF文件时出错，请确保文件格式正确');
      }
    },

    async compressPDF() {
      if (!this.originalPDF) return;

      try {
        // 显示压缩中状态
        this.isCompressing = true;
        this.compressedPDF = null;
        this.compressedFileSize = 0;
        this.compressedPageCount = 0;

        console.log('准备压缩', this.originalPDF)

        // 压缩PDF
        const pdfRes = await compress(this.originalPDF, {
          preset: 'max', // 'lossless' | 'balanced' | 'max'
          chunkSize: 5, // 减小分片大小，降低内存压力（默认10，大文件易出错）
          onProgress: (event) => {
            console.log(`${event.phase}: ${event.progress}%`);
            if (event.message) {
              console.log(event.message); // e.g., "Compressing page 5/98..."
            }
          },
          gracefulDegradation: true, // 出错时自动降级到更轻量的模式（默认开启，显式声明更保险）
          preserveMetadata: false, // 关闭元数据保留，减少处理复杂度
          targetDPI: 150, // 适中的DPI，避免过度压缩导致的图像处理失败
          jpegQuality: 0.9, // 降低压缩强度（默认0.5可能过强）
          mergeStrategy: 'main', // 改用主线程合并（worker合并可能有线程通信问题）
          timeout: 600000 // 延长超时时间到10分钟，避免大文件处理超时
        });

        const pdfBytes = pdfRes.pdf

        console.log('压缩完成', pdfBytes)


        // 重新加载压缩后的PDF来获取页数
        const compressedPdfDoc = await PDFDocument.load(pdfBytes);
        this.compressedPageCount = compressedPdfDoc.getPageCount();

        // 转换为Blob并计算大小
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        this.compressedFileSize = blob.size;

        // 转换为Data URL用于下载
        const reader = new FileReader();
        reader.onload = (e) => {
          this.compressedPDF = e.target.result;
          this.isCompressing = false;
          console.log('PDF压缩完成');
        };
        reader.onerror = () => {
          throw new Error('文件读取失败');
        };
        reader.readAsDataURL(blob);

      } catch (error) {
        if (error instanceof CompressionError) {
          console.error('Compression failed:', error.message);
          console.log('Attempted preset:', error.attemptedPreset);
          console.log('Original size:', error.originalSize);
          console.log('Phase:', error.phase);
          alert(`压缩PDF时出错: ${error.message || '请重试'}`);
        } else {
          console.error('压缩出错:', error);
          alert(`压缩出错: ${error.message || '请重试'}`);
        }
      } finally {
        // 无论成功失败都关闭加载状态
        this.isCompressing = false;
      }
    },

    downloadPDF() {
      if (!this.compressedPDF) return;

      const link = document.createElement('a');
      link.download = `compressed_${this.originalFileName}`;
      link.href = this.compressedPDF;
      link.click();
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
}
</script>

<style lang="less" scoped>
.e-pdf {
  font-family: 'Arial', sans-serif;
  background-color: #f5f5f5;
  padding: 15px;
  display: flex;
  min-height: 90vh;

  .container {
    flex: 1;
    display: flex;
    gap: 20px;
    max-width: 95vw;
    margin: 0 auto;

    .control-panel {
      width: 300px;
      height: 90vh;
      overflow-y: auto;
      overflow-x: hidden;
      background-color: white;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      gap: 20px;

      .upload-section {
        margin-bottom: 7px;

        button {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 8px;
          font-size: 15px;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-bottom: 12px;
          width: 100%;
        }

        button:hover {
          background-color: #3367d6;
        }
      }

      .actions {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .btn {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 10px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;

          &:hover {
            background-color: #3367d6;
          }

          &:disabled {
            background-color: #bdc3c7;
            cursor: not-allowed;
          }

          &.btn-success {
            background-color: #2ecc71;

            &:hover {
              background-color: #27ae60;
            }
          }
        }
      }
    }

    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 20px;
      min-height: 90vh;

      .header {
        background-color: white;
        border-radius: 8px;
        padding: 15px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 15px;

          .pdf-info-cards {
            display: flex;
            gap: 20px;
            width: 100%;
            margin-top: 10px;
          }

          .info-card {
            flex: 1;
            padding: 15px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .info-card.original {
            background-color: #f8f9fa;
            border-left: 4px solid #4285f4;
          }

          .info-card.compressed {
            background-color: #f0f8f0;
            border-left: 4px solid #2ecc71;
          }

          .card-header {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: bold;
            font-size: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          }

          .card-header .icon {
            font-size: 18px;
          }

          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .info-label {
            font-size: 13px;
            color: #666;
          }

          .info-value {
            font-size: 13px;
            font-weight: bold;
            color: #333;
          }

          .info-value.success {
            color: #2ecc71;
          }

          .info-value.reduction {
            color: #e74c3c;
          }
        }
      }

      .preview-container {
        background-color: white;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        flex: 1;
        overflow: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 300px;

        .upload-prompt {
          text-align: center;
          color: #999;

          .upload-icon {
            font-size: 48px;
            margin-bottom: 15px;
          }

          p {
            font-size: 16px;
          }
        }

        .pdf-preview {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          gap: 15px;
          position: relative;

          .pdf-preview-box {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px;
            box-sizing: border-box;
            position: relative;

            .pdf-icon {
              font-size: 64px;
              color: #e74c3c;
            }

            .compression-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(255, 255, 255, 0.9);
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 10px;

              .spinner {
                width: 40px;
                height: 40px;
                border: 4px solid #f3f3f3;
                border-top: 4px solid #4285f4;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }

              p {
                margin: 0;
                font-size: 14px;
                color: #666;
              }
            }
          }

          .pdf-info {
            text-align: center;
            font-size: 14px;
            color: #666;

            .compression-info {
              color: #2ecc71;
              font-weight: bold;
              margin-top: 10px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 900px) {
  .e-pdf .container {
    flex-direction: column;
  }

  .e-pdf .control-panel {
    width: 100%;
    height: auto;
    max-height: 50vh;
  }

  .e-pdf .right-panel {
    min-height: auto;
  }

  .e-pdf .preview-container {
    max-height: 500px;
  }

  .e-pdf .pdf-preview {
    flex-direction: column;
    align-items: center;
  }

  .e-pdf .header-content {
    flex-direction: column;
    align-items: stretch !important;

    .pdf-info-cards {
      flex-direction: column;
    }
  }
}

@media (max-width: 600px) {
  .e-pdf .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>